import { getRequestTokenPair } from "api/auth"
import { DOCUMENT_ENDPOINT, INTELLICHECKER_PRESET_ENDPOINT, INTELLICHECKER_STATS_ENDPOINT, PAYMENTS_IC_COUNTER_ENDPOINT } from "api/endpoints"
import { createAuthorizedFetch } from "api/fetch"
import { fetchCurrentUser } from "api/user"
import { LoginCheck } from "components/LoginCheck"
import { Navbar } from "components/Navbar"
import { useUser } from "hooks/user"
import { ICError, run as runIntellichecker } from "lib/intellichecker"
import { GetServerSideProps } from "next"
import Head from "next/head"
import { useRouter } from "next/router"
import { useEffect, useId, useState } from "react"
import Dropzone, { useDropzone } from "react-dropzone"
import Markdown from "react-markdown"
import ReactSelect from "react-select"
import { Document as DocumentModel, DocumentKind } from 'models/document'
import { icPresetOptions } from "data/ic-presets"
import { useAPIFetch } from "hooks/fetch"
import { InputNumber, Spin } from "antd"
import { toast } from "react-toastify"
import { NamedModalWindow } from "components/ModalWindow"
import { FormField } from "components/Form"
import { inputClassNames } from "components/Input"
import { ButtonV2 } from "components/Button"
import { twMerge } from "tailwind-merge"

const presets = new Map([
  [
    'kvtp-original',
    [
      'Название пишется с прописной буквы, в конце точка не ставится',
      'Резюме должно начинаться с заголовка "Резюме"',
      'После резюме должны присутствовать "Ключевые слова"',
      'Количество слов в резюме не должно превышать 200',
      'Обязательные Разделы резюме: "Цель", "Материал и методы" или "Материалы и методы", "Результаты", "Заключение"',
      'При цитировании статьи должен присутствовать DOI/EDN',
      'При цитировании книги должен присутствовать ISBN',
      'При цитировании Web-ссылки должен присутствовать её полный URL',
      'При цитировании статьи авторы указываются через запятую',
      'Цитирование статьи должно начинаться со списка авторов',
      'В библиографическом описании приводятся фамилии авторов до трех, после чего, для отечественных публикаций следует указать "и др.", для зарубежных – "et al."',
      // 'При цитировании статьи сначала указываются авторы, потом название статьи (и в русском, и в английском цитировании)',
      'При цитировании русскоязычной статьи, сначала она цитируется на английском, потом на русском',
      // 'При цитировании русскоязычной статьи, цитирование на русском начинается с "(In Russ.)"',
      'При цитировании рускоязычной статьи, ее англоязычная часть должна завершаться на "(In Russ.)"',
      'Название таблицы располагается над таблицей',
      'Подпись к рисунку располагается под рисунком',
      'Поля страницы должны быть 2 см',
      'Межстрочный интервал должен быть 1,5',
      'Выравнивание текста должно быть "По ширине"',
      'Выравнивание названия должно быть "По центру"',
    ],
  ],
  [
    'kvtp-litreview',
    [
      'Название пишется с прописной буквы, в конце точка не ставится',
      'Резюме должно начинаться с заголовка "Резюме"',
      'После резюме должны присутствовать "Ключевые слова"',
      'Количество слов в резюме не должно превышать 250',
      'При цитировании статьи должен присутствовать DOI/EDN',
      'При цитировании книги должен присутствовать ISBN',
      'При цитировании Web-ссылки должен присутствовать её полный URL',
      'При цитировании статьи авторы указываются через запятую',
      'Цитирование статьи должно начинаться со списка авторов',
      'В библиографическом описании приводятся фамилии авторов до трех, после чего, для отечественных публикаций следует указать "и др.", для зарубежных – "et al."',
      // 'При цитировании статьи сначала указываются авторы, потом название статьи (и в русском, и в английском цитировании)',
      'При цитировании русскоязычной статьи, сначала она цитируется на английском, потом на русском',
      // 'При цитировании русскоязычной статьи, цитирование на русском начинается с "(In Russ.)"',
      'При цитировании рускоязычной статьи, ее англоязычная часть должна завершаться на "(In Russ.)"',
      'Название таблицы располагается над таблицей',
      'Подпись к рисунку располагается под рисунком',
      'Поля страницы должны быть 2 см',
      'Межстрочный интервал должен быть 1,5',
      'Выравнивание текста должно быть "По ширине"',
      'Выравнивание названия должно быть "По центру"',
    ],
  ],
  [
    'kvtp-clinicalcase',
    [
      'Название пишется с прописной буквы, в конце точка не ставится',
      'Резюме должно начинаться с заголовка "Резюме"',
      'После резюме должны присутствовать "Ключевые слова"',
      'Количество слов в резюме не должно превышать 200',
      'Обязательные разделы резюме: "Введение", "Краткое описание" и "Дискуссия"',
      'Обязательные разделы текста: "Введение", "Клинический случай", "Обсуждение", "Заключение"',
      'При цитировании статьи должен присутствовать DOI/EDN',
      'При цитировании книги должен присутствовать ISBN',
      'При цитировании Web-ссылки должен присутствовать её полный URL',
      'При цитировании статьи авторы указываются через запятую',
      'Цитирование статьи должно начинаться со списка авторов',
      'В библиографическом описании приводятся фамилии авторов до трех, после чего, для отечественных публикаций следует указать "и др.", для зарубежных – "et al."',
      // 'При цитировании статьи сначала указываются авторы, потом название статьи (и в русском, и в английском цитировании)',
      'При цитировании русскоязычной статьи, сначала она цитируется на английском, потом на русском',
      // 'При цитировании русскоязычной статьи, цитирование на русском начинается с "(In Russ.)"',
      'При цитировании рускоязычной статьи, ее англоязычная часть должна завершаться на "(In Russ.)"',
      'Название таблицы располагается над таблицей',
      'Подпись к рисунку располагается под рисунком',
      'Поля страницы должны быть 2 см',
      'Межстрочный интервал должен быть 1,5',
      'Выравнивание текста должно быть "По ширине"',
      'Выравнивание названия должно быть "По центру"',
    ],
  ],

  [
    'psmp-original',
    [
      'Название пишется с прописной буквы, в конце точка не ставится',
      'Резюме должно начинаться с заголовка "Резюме"',
      'После резюме должны присутствовать "Ключевые слова"',
      'Количество слов в резюме не должно превышать 200',
      'Обязательные Разделы резюме: "Цель", "Материал и методы" или "Материалы и методы", "Результаты", "Заключение"',
      'При цитировании статьи должен присутствовать DOI/EDN',
      'При цитировании книги должен присутствовать ISBN',
      'При цитировании Web-ссылки должен присутствовать её полный URL',
      'При цитировании статьи авторы указываются через запятую',
      'Цитирование статьи должно начинаться со списка авторов',
      'В библиографическом описании приводятся фамилии авторов до трех, после чего, для отечественных публикаций следует указать "и др.", для зарубежных – "et al."',
      // 'При цитировании статьи сначала указываются авторы, потом название статьи (и в русском, и в английском цитировании)',
      'При цитировании русскоязычной статьи, сначала она цитируется на английском, потом на русском',
      // 'При цитировании русскоязычной статьи, цитирование на русском начинается с "(In Russ.)"',
      'При цитировании рускоязычной статьи, ее англоязычная часть должна завершаться на "(In Russ.)"',
      'Название таблицы располагается над таблицей',
      'Подпись к рисунку располагается под рисунком',
      'Поля страницы должны быть 2 см',
      'Межстрочный интервал должен быть 1,5',
      'Выравнивание текста должно быть "По ширине"',
      'Выравнивание названия должно быть "По центру"',
    ],
  ],
  [
    'psmp-litreview',
    [
      'Название пишется с прописной буквы, в конце точка не ставится',
      'Резюме должно начинаться с заголовка "Резюме"',
      'После резюме должны присутствовать "Ключевые слова"',
      'Количество слов в резюме не должно превышать 250',
      'При цитировании статьи должен присутствовать DOI/EDN',
      'При цитировании книги должен присутствовать ISBN',
      'При цитировании Web-ссылки должен присутствовать её полный URL',
      'При цитировании статьи авторы указываются через запятую',
      'Цитирование статьи должно начинаться со списка авторов',
      'При цитировании статьи должно быть указано не более 3 авторов, после чего ставиться "et al" или "и др."',
      // 'При цитировании статьи сначала указываются авторы, потом название статьи (и в русском, и в английском цитировании)',
      'При цитировании русскоязычной статьи, сначала она цитируется на английском, потом на русском',
      // 'При цитировании русскоязычной статьи, цитирование на русском начинается с "(In Russ.)"',
      'При цитировании рускоязычной статьи, ее англоязычная часть должна завершаться на "(In Russ.)"',
      'Название таблицы располагается над таблицей',
      'Подпись к рисунку располагается под рисунком',
      'Поля страницы должны быть 2 см',
      'Межстрочный интервал должен быть 1,5',
      'Выравнивание текста должно быть "По ширине"',
      'Выравнивание названия должно быть "По центру"',
    ],
  ],
  [
    'psmp-clinicalcase',
    [
      'Название пишется с прописной буквы, в конце точка не ставится',
      'Резюме должно начинаться с заголовка "Резюме"',
      'После резюме должны присутствовать "Ключевые слова"',
      'Количество слов в резюме не должно превышать 200',
      'Обязательные разделы резюме: "Введение", "Краткое описание" и "Дискуссия"',
      'Обязательные разделы текста: "Введение", "Клинический случай", "Обсуждение", "Заключение"',
      'При цитировании статьи должен присутствовать DOI/EDN',
      'При цитировании книги должен присутствовать ISBN',
      'При цитировании Web-ссылки должен присутствовать её полный URL',
      'При цитировании статьи авторы указываются через запятую',
      'Цитирование статьи должно начинаться со списка авторов',
      'В библиографическом описании приводятся фамилии авторов до трех, после чего, для отечественных публикаций следует указать "и др.", для зарубежных – "et al."',
      // 'При цитировании статьи сначала указываются авторы, потом название статьи (и в русском, и в английском цитировании)',
      'При цитировании русскоязычной статьи, сначала она цитируется на английском, потом на русском',
      // 'При цитировании русскоязычной статьи, цитирование на русском начинается с "(In Russ.)"',
      'При цитировании рускоязычной статьи, ее англоязычная часть должна завершаться на "(In Russ.)"',
      'Название таблицы располагается над таблицей',
      'Подпись к рисунку располагается под рисунком',
      'Поля страницы должны быть 2 см',
      'Межстрочный интервал должен быть 1,5',
      'Выравнивание текста должно быть "По ширине"',
      'Выравнивание названия должно быть "По центру"',
    ],
  ],


  [
    'rcj-litreview',
    [
      'Название пишется с прописной буквы, в конце точка не ставится',
      'Резюме должно начинаться с заголовка "Резюме"',
      'После резюме должны присутствовать "Ключевые слова"',
      'Количество слов в резюме не должно превышать 250',
      'При цитировании статьи должен присутствовать DOI/EDN',
      'При цитировании книги должен присутствовать ISBN',
      'При цитировании Web-ссылки должен присутствовать её полный URL',
      'При цитировании статьи авторы указываются через запятую',
      'Цитирование статьи должно начинаться со списка авторов',
      'При цитировании статьи должно быть указано не более 3 авторов, после чего ставиться "et al" или "и др."',
      // 'При цитировании статьи сначала указываются авторы, потом название статьи (и в русском, и в английском цитировании)',
      'При цитировании русскоязычной статьи, сначала она цитируется на английском, потом на русском',
      // 'При цитировании русскоязычной статьи, цитирование на русском начинается с "(In Russ.)"',
      'При цитировании рускоязычной статьи, ее англоязычная часть должна завершаться на "(In Russ.)"',
      'Название таблицы располагается над таблицей',
      'Подпись к рисунку располагается под рисунком',
      'Поля страницы должны быть 2 см',
      'Межстрочный интервал должен быть 1,5',
      'Выравнивание текста должно быть "По ширине"',
      'Выравнивание названия должно быть "По центру"',
    ],
  ],
  [
    'rcj-original',
    [
      'Название пишется с прописной буквы, в конце точка не ставится',
      'Резюме должно начинаться с заголовка "Резюме"',
      'После резюме должны присутствовать "Ключевые слова"',
      'Количество слов в резюме не должно превышать 200',
      'Обязательные Разделы резюме: "Цель", "Материал и методы" или "Материалы и методы", "Результаты", "Заключение"',
      'При цитировании статьи должен присутствовать DOI/EDN',
      'При цитировании книги должен присутствовать ISBN',
      'При цитировании Web-ссылки должен присутствовать её полный URL',
      'При цитировании статьи авторы указываются через запятую',
      'Цитирование статьи должно начинаться со списка авторов',
      'В библиографическом описании приводятся фамилии авторов до трех, после чего, для отечественных публикаций следует указать "и др.", для зарубежных – "et al."',
      // 'При цитировании статьи сначала указываются авторы, потом название статьи (и в русском, и в английском цитировании)',
      'При цитировании русскоязычной статьи, сначала она цитируется на английском, потом на русском',
      // 'При цитировании русскоязычной статьи, цитирование на русском начинается с "(In Russ.)"',
      'При цитировании рускоязычной статьи, ее англоязычная часть должна завершаться на "(In Russ.)"',
      'Название таблицы располагается над таблицей',
      'Подпись к рисунку располагается под рисунком',
      'Поля страницы должны быть 2 см',
      'Межстрочный интервал должен быть 1,5',
      'Выравнивание текста должно быть "По ширине"',
      'Выравнивание названия должно быть "По центру"',
    ],
  ],
  [
    'profmed-original',
    [
      'Правила в разработке'
    ]
  ]
])


type ICPageProps = {
  preset: string
  documentBase64?: string
  ic_counter?: number
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { preset: presetParam, documentid: documentIdParam, projectid: projectIdParam } = context.query

  let preset = 'psmp-original'

  if (typeof presetParam === 'string' && presets.get(presetParam)) {
    preset = presetParam
  }

  const props: ICPageProps = { preset }


  if (!getRequestTokenPair(context.req))
    return { props }

  const apiFetch = createAuthorizedFetch(getRequestTokenPair(context.req)?.accessToken)
  const user = await fetchCurrentUser(apiFetch)
  if (!user)
    return { props }

  props.ic_counter = user.ic_counter

  if (typeof documentIdParam !== 'string' || typeof projectIdParam !== 'string')
    return { props }

  await fetch(process.env.NEXT_PUBLIC_DOCUMENTSERVER_SHIM_URL! + '/' + documentIdParam + '/forcesave', { method: 'POST' })

  const doc = await apiFetch(DOCUMENT_ENDPOINT(projectIdParam, documentIdParam))
  if (!doc.ok) {
    return { props }
  }
  const docData = await doc.json() as DocumentModel
  if (docData.content.length === 0 || docData.legacy || docData.kind !== DocumentKind.MSWORD)
    return { props }

  props.documentBase64 = docData.content

  return { props }
}

function AddChecksModal() {
  const [open, setOpen] = useState(false)
  const [counter, setCounter] = useState<number | null>(10)
  const apiFetch = useAPIFetch()
  const handlePayment = async () => {
    try {
      const resp = await apiFetch(PAYMENTS_IC_COUNTER_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify({
          count: counter,
        })
      })
      const j = await resp.json()
      window.location.href = j['payment_link']
    } catch (e) {
      console.error('failed to fetch the ic counter payment link', e)
      return
    }
  }
  return <>
    <button className="text-sm p-0.5 m-0 rounded-xl bg-secondary text-white" onClick={() => setOpen(true)}>Добавить проверки</button>
    <NamedModalWindow name="Количество проверок" className="w-15" open={open} close={() => setOpen(false)} closable>
      <InputNumber className={twMerge(inputClassNames, "w-full")} min={1} max={99} value={counter} onChange={c => setCounter(c)} />
      <ButtonV2 disabled={!counter} onClick={() => handlePayment()} className="w-full">
        Оплатить
      </ButtonV2>
    </NamedModalWindow>
  </>
}

export default function Intellichecker({ preset: initialPreset, documentBase64: initialContentBase64, ic_counter: icCounterInitial }: ICPageProps) { // TODO: content on frontend, since files can be large.
  const [user, setUser] = useUser()
  const [icCounter, setIcCounter] = useState<number | undefined>(icCounterInitial)
  const fileUploadId = useId()
  const apiFetch = useAPIFetch()
  const router = useRouter()

  const [errors, setErrors] = useState<ICError[]>()
  const [currentFile, setCurrentFile] = useState<File>()
  const [preset, setPreset] = useState<string>(initialPreset)
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleUpload = async (blob: Blob) => {
    setLoading(true)
    const resp = await apiFetch(INTELLICHECKER_PRESET_ENDPOINT(preset), {
      method: 'POST',
      body: blob,
    })

    if (resp.status === 401) { // If the tokens are old
      router.push('/login?redir=' + encodeURIComponent('/intellichecker'))
      return
    }

    if (resp.status === 403) {
      toast.error('Превышено количество доступных проверок')
      setErrors(undefined)
      setLoading(false)
      return
    }

    // const errs = await runIntellichecker(blob, preset!)
    setErrors((await resp.json()) as ICError[])
    setLoading(false)
    setIcCounter(icCounter!-1)

    // fetch(INTELLICHECKER_STATS_ENDPOINT, {
    //   method: 'POST',
    //   body: JSON.stringify({ preset, user_id: user?.id })
    // })
  }



  useEffect(() => {
    if (!initialContentBase64) return
    handleUpload(new Blob([Buffer.from(initialContentBase64, 'base64')]))
  }, [])

  // useEffect(() => {
  //   if (user) setIcCounter(user.ic_counter)
  // }, [user])


  return <>
    <Head>
      <title>Intellichecker</title>
    </Head>
    <div>
      <Navbar user={user} static />
      <div className="mx-4 4xl:mx-15 mt-4 pb-2">
        <h1 className="text-3xl 2xl:text-5xl font-semibold text-secondary">Проверка рукописи</h1>
        {icCounter && <div className="flex flex-row gap-2 pr-1 items-center mt-2 w-[50%] justify-between">
          <p className="text-sm text-secondary">Оставшееся количество проверок: <b>{icCounter}</b></p>
          <AddChecksModal />
        </div>}
        <div className="flex flex-row gap-2 mt-3 3xl:mt-5 w-full">
          <div className="w-[50%]">
            <h2 className="text-2xl font-semibold">Рукопись</h2>
            <label htmlFor={fileUploadId} className="w-full border border-grey border-dashed flex justify-center gap-1 p-1 rounded-xl mt-1 cursor-pointer"
              id="ic-upload-btn"
              onDrop={e => {
                e.preventDefault()
                // alert('ehllo')
                handleUpload(new Blob(Array.from(e.dataTransfer.files)))
                setUploading(false)
              }} onDragOver={(event) => {
                event.preventDefault()
                setUploading(true)
              }} onDragLeave={e => {
                e?.preventDefault()
                setUploading(false)
              }}>
              <input type="file" id={fileUploadId} className="hidden" accept=".docx" onChange={e => {
                e.preventDefault()
                // alert('hi')
                handleUpload(new Blob(Array.from(e.target.files!)))
                setCurrentFile(e.target.files!.item(0)!)
                e.currentTarget.value = ""
              }} />
              {/* {currentFile ? <> */}
              {/*   <img src="/images/docx.svg" width="24px" height="24px" /> */}
              {/*   <p>{currentFile.name}</p> */}
              {/* </> : <> */}
              <img src="/images/upload.svg" width="24px" height="24px" />
              {!uploading && <p>Загрузить файл</p>}
              {/* </> */}
              {/* } */}
            </label>
            <h2 className="text-2xl font-semibold mt-2">Набор требований журнала</h2>
            <ReactSelect
              // tooltip="Стиль"
              options={Array.from(icPresetOptions.values())}
              value={{ label: icPresetOptions.get(preset)!.label, value: preset! }}
              onChange={v => {
                setPreset(v!.value)
              }}
              instanceId="preset-select"
              id="preset-select"
              menuPosition="fixed"
              placeholder="Выберите набор правил"
              styles={{
                placeholder: (base, state) => ({
                  ...base,
                  color: 'var(--color-grey)',
                }),
                control: (base, state) => ({ ...base, borderRadius: 5 })
              }}
              className="mt-1"
            />

            <ul className="mt-1 px-1 list-disc flex flex-col gap-0.5">
              {presets.get(preset!)!.map(v => <li key={v}>{v}</li>)}
            </ul>
          </div>
          <div className="w-[50%]">
            <h2 className="text-2xl font-semibold">Результаты анализа</h2>
            {!!errors?.length && <div className="flex flex-row gap-1 mt-1">
              <span className="flex flex-row gap-0.5 select-none"><img src="/images/intellichecker/error.svg" className="w-1 h-1" /> Необходимо исправить</span>
              <span className="flex flex-row gap-0.5 select-none"><img src="/images/intellichecker/warn.svg" className="w-1 h-1" /> Рекомендуется исправить</span>
            </div>}
            <ol className="whitespace-break-spaces p-1.5-new mt-1  list-decimal border border-light-grey rounded-xl flex flex-col gap-0.5">
              {loading ? <Spin /> : errors ? errors?.map(v => <li key={v.message} className="flex flex-row gap-0.5">
                <img src={v.level === 'error' ? "/images/intellichecker/error.svg" : "/images/intellichecker/warn.svg"} className="w-1 h-1 select-none" />
                <Markdown
                  components={{
                    a({ node, children, ...props }) {
                      props.target = "_blank";
                      props.rel = "noopener noreferrer";

                      return <a {...props}>{children}</a>;
                    },
                  }}
                  className="ml-0.5 prose"
                >{v.message}</Markdown>
              </li>) : <p className="text-grey">Здесь пока ничего нет</p>}
            </ol>
          </div>
        </div>
      </div>
    </div>
  </>
}
